/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   RunDetailDrawer.tsx
 *     Feature         :   191-pipeline-monitoring-platform
 *     Description     :   Family-adaptive run detail drawer — run header + steps (incl.
 *                         preflight, flagged) + event logs, for any registered process.
 *                         Feature parity with the legacy ODS/DW detail modals.
 *     Constitution    :   v1.18.0 | Principle XXXVII (Code Header Standards)
 *     Author          :   Peter Dobler
 *     Created         :   2026-06-11
 * =========================================================================================
 */

import React, { useState } from 'react';
import type { PipelineLogSeverity } from '@shared/types/monitoredPipelines';
import { usePipelineRunDetail } from '../../hooks/useMonitoredPipelines';
import { DualScrollTable, usePagination, PaginationControls } from '../common/TableControls';
import { LoadingSpinner } from './LoadingSpinner';
import { ErrorBanner } from './ErrorBanner';
import {
  PipelineStatusBadge,
  formatDuration,
  formatDateTime,
  formatNumber,
} from './PipelineStatusBadge';

export interface RunDetailDrawerProps {
  pipelineKey: string | null;
  batchId: string | null;
  pipelineDisplayName?: string;
  onClose: () => void;
}

type DetailTab = 'steps' | 'logs';

const SEVERITY_CLASSES: Record<PipelineLogSeverity, string> = {
  INFO:  'text-[var(--color-text-secondary)]',
  WARN:  'text-[var(--color-status-warning)]',
  ERROR: 'text-[var(--color-status-danger)]',
};

function HeaderStat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-[var(--color-text-secondary)]">{label}</div>
      <div className="text-sm font-medium text-[var(--color-text-primary)]">{value}</div>
    </div>
  );
}

export function RunDetailDrawer({
  pipelineKey,
  batchId,
  pipelineDisplayName,
  onClose,
}: RunDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>('steps');
  const { data, isLoading, error } = usePipelineRunDetail(pipelineKey, batchId);

  const run = data?.run;
  const steps = data?.steps ?? [];
  const logs = data?.logs ?? [];
  const preflightCount = steps.filter((s) => s.isPreflightStep).length;
  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } = usePagination(steps);

  if (!pipelineKey || !batchId) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="run-detail-drawer">
      {/* Transparent-but-blocking backdrop (Spec 197): blocks input, keeps the
          page visible behind, and clicking it does NOT close. */}
      <div className="absolute inset-0 bg-transparent" aria-hidden="true" />

      <div className="relative h-full w-full max-w-2xl flex flex-col border-l border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-2xl">
        {/* Header */}
        <div className="border-b border-[var(--color-border)] p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs text-[var(--color-text-secondary)]">
                {pipelineDisplayName ?? pipelineKey}
              </div>
              <h2 className="text-base font-semibold text-[var(--color-text-primary)] font-mono truncate" title={batchId}>
                {batchId}
              </h2>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {run && <PipelineStatusBadge status={run.status} size="md" />}
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="drawer-close"
                className="rounded p-1.5 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)] transition-colors"
              >
                ✕
              </button>
            </div>
          </div>

          {run && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <HeaderStat label="Started" value={formatDateTime(run.startTime)} />
              <HeaderStat label="Duration" value={formatDuration(run.durationSeconds)} />
              <HeaderStat
                label="Items ✓ / ✗ / skipped"
                value={`${formatNumber(run.itemsSucceeded)} / ${formatNumber(run.itemsFailed)} / ${formatNumber(run.itemsSkipped)}`}
              />
              <HeaderStat label="Rows processed" value={formatNumber(run.totalRowsProcessed)} />
              <HeaderStat label="Instance" value={run.instance} />
              <HeaderStat label="Mode" value={run.mode} />
              {run.sourceName && <HeaderStat label="Source" value={run.sourceName} />}
              <HeaderStat label="Environment" value={run.environment} />
            </div>
          )}

          {run?.errorMessage && (
            <div className="rounded border border-[var(--color-status-danger)] bg-[var(--color-status-danger)]/10 p-2.5 text-xs text-[var(--color-status-danger)] whitespace-pre-wrap break-words">
              {run.errorMessage}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[var(--color-border)] px-4">
          {([
            { key: 'steps', label: `Steps (${steps.length})${preflightCount ? ` · ${preflightCount} preflight` : ''}` },
            { key: 'logs',  label: `Logs (${logs.length})` },
          ] as { key: DetailTab; label: string }[]).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              data-testid={`drawer-tab-${key}`}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === key
                  ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                  : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {error ? (
            <ErrorBanner message="Failed to load run detail" />
          ) : isLoading ? (
            <LoadingSpinner />
          ) : activeTab === 'steps' ? (
            steps.length === 0 ? (
              <p className="text-sm text-[var(--color-text-secondary)] text-center py-8">No steps recorded for this run.</p>
            ) : (
              <>
              <DualScrollTable>
              <table className="min-w-full divide-y divide-[var(--color-border)] text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
                    <th className="px-2 py-2">Step</th>
                    <th className="px-2 py-2">Stage</th>
                    <th className="px-2 py-2">Duration</th>
                    <th className="px-2 py-2 text-right">Rows</th>
                    <th className="px-2 py-2 text-right">Retries</th>
                    <th className="px-2 py-2">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                  {pageItems.map((step) => (
                    <React.Fragment key={step.taskId}>
                      <tr data-testid={`step-row-${step.taskId}`}>
                        <td className="px-2 py-2 font-medium text-[var(--color-text-primary)]">
                          {step.isPreflightStep && (
                            <span className="mr-1.5 inline-flex rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 px-1.5 py-0.5 text-[10px] font-semibold uppercase">
                              Preflight
                            </span>
                          )}
                          {step.stepName}
                        </td>
                        <td className="px-2 py-2 text-[var(--color-text-secondary)]">{step.stage}</td>
                        <td className="px-2 py-2 text-[var(--color-text-secondary)]">{formatDuration(step.durationSeconds)}</td>
                        <td className="px-2 py-2 text-right text-[var(--color-text-secondary)]">{formatNumber(step.rowCount)}</td>
                        <td className="px-2 py-2 text-right text-[var(--color-text-secondary)]">{step.retryCount ?? '—'}</td>
                        <td className="px-2 py-2"><PipelineStatusBadge status={step.status} /></td>
                      </tr>
                      {step.errorMessage && (
                        <tr>
                          <td colSpan={6} className="px-2 pb-2">
                            <div className="rounded bg-[var(--color-status-danger)]/10 border border-[var(--color-status-danger)] p-2 text-xs text-[var(--color-status-danger)] whitespace-pre-wrap break-words">
                              {step.errorMessage}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              </DualScrollTable>
              <PaginationControls page={page} pageSize={pageSize} total={total} totalPages={totalPages} start={start} shown={pageItems.length} onPage={setPage} onPageSize={changeSize} />
              </>
            )
          ) : logs.length === 0 ? (
            <p className="text-sm text-[var(--color-text-secondary)] text-center py-8">No log events for this run.</p>
          ) : (
            <ol className="space-y-1.5 font-mono text-xs">
              {logs.map((log) => (
                <li key={log.eventId} className="flex gap-2" data-testid={`log-row-${log.eventId}`}>
                  <span className="text-[var(--color-text-secondary)] shrink-0">{formatDateTime(log.timestamp)}</span>
                  <span className={`font-semibold shrink-0 w-12 ${SEVERITY_CLASSES[log.severity] ?? SEVERITY_CLASSES.INFO}`}>
                    {log.severity}
                  </span>
                  <span className="text-[var(--color-text-secondary)] shrink-0">[{log.stage}]</span>
                  <span className="text-[var(--color-text-primary)] break-words">{log.message}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
